import { useEffect, useState } from "react";
import { CloseIcon } from "@app/lib/events-admin-icons";
import {
  Button,
  Dialog,
  Modal,
  ModalOverlay,
  TextAreaBase,
} from "@its-engineering-and-development/ui-foundation/components";

interface ConfirmActionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  inputLabel: string;
  inputPlaceholder?: string;
  showReasonInput?: boolean;
  isReasonRequired?: boolean;
  confirmLabel: string;
  loadingLabel: string;
  cancelLabel?: string;
  isLoading?: boolean;
  errorMessage?: string | null;
  onConfirm: (reason: string) => Promise<void> | void;
}

export function ConfirmActionDialog({
  isOpen,
  onOpenChange,
  title,
  description,
  inputLabel,
  inputPlaceholder,
  showReasonInput = true,
  isReasonRequired = true,
  confirmLabel,
  loadingLabel,
  cancelLabel = "Close",
  isLoading = false,
  errorMessage,
  onConfirm,
}: ConfirmActionDialogProps) {
  const [reason, setReason] = useState("");
  const [showRequiredError, setShowRequiredError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setShowRequiredError(false);
    }
  }, [isOpen]);

  const trimmedReason = reason.trim();
  const isReasonMissing =
    showReasonInput && isReasonRequired && trimmedReason.length === 0;

  const handleConfirm = async () => {
    if (isReasonMissing) {
      setShowRequiredError(true);
      return;
    }

    await onConfirm(trimmedReason);
  };

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!isLoading) onOpenChange(open);
      }}
      isDismissable={!isLoading}
    >
      <Modal
        className="events-modal events-modal--compact"
        isDismissable={!isLoading}
      >
        <Dialog className="events-dialog" aria-label={title}>
          <div className="events-modal__header">
            <h3>{title}</h3>
            <button
              type="button"
              className="events-dialog__close"
              onClick={() => onOpenChange(false)}
              aria-label="Close modal"
              disabled={isLoading}
            >
              <CloseIcon className="size-4" />
            </button>
          </div>

          {description ? (
            <p className="events-page__subtitle">{description}</p>
          ) : null}

          {showReasonInput ? (
            <label className="events-form-label">
              {inputLabel}
              <TextAreaBase
                rows={5}
                value={reason}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setReason(nextValue);
                  if (showRequiredError && nextValue.trim().length > 0) {
                    setShowRequiredError(false);
                  }
                }}
                placeholder={inputPlaceholder}
                className={showRequiredError ? "ring-error_subtle" : undefined}
              />
              {showRequiredError ? (
                <span className="events-form-error">Reason is required.</span>
              ) : null}
            </label>
          ) : null}

          {errorMessage ? (
            <p className="events-form-error">{errorMessage}</p>
          ) : null}

          <div className="events-modal__footer">
            <Button
              size="lg"
              color="primary"
              className="events-solid-button events-solid-button--danger"
              onPress={() => void handleConfirm()}
              isDisabled={isReasonMissing || isLoading}
            >
              {isLoading ? loadingLabel : confirmLabel}
            </Button>
            <Button
              size="lg"
              color="secondary"
              className="events-outline-button"
              onPress={() => onOpenChange(false)}
              isDisabled={isLoading}
            >
              {cancelLabel}
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
